import {
  lendingApplicationMessages,
  lendingOnboardingSteps,
  applicationDefaults,
  storageKeys,
  income_frequencies,
  errorMessages
} from 'utils';
import { request } from './request';
import { generateUUID, isoDateMonthsAgo } from 'utils/formatting';
import { Cache } from './cache.service';
import { Environment } from 'Environment';
import config from '../config.json';
import { getLoan } from './getLoan';

const sessionId = generateUUID();

// OMF rejects applications where employment is shorter than 3 months, so this placeholder
// start date is kept comfortably past that minimum instead of being hardcoded and going stale.
const employmentStartDate = isoDateMonthsAgo(6);

const omfRequest = (path, fetchOptions, extra = {}) =>
  request(path, {
    fetchOptions: {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        'X-Api-OriginChannel': 'FE_COMPARE_COMPLETE',
        ...(fetchOptions.headers || {}),
      },
    },
    baseURLOverwrite: Environment.current.apiHost,
    tealiumErrorLogger: null,
    suppressTealiumEventOnError: false,
    showToastOnError: false,
    ignoreErrorCodes: [],
    ...extra,
  });

const getInterpolatedValue = (min, max, step) => {
  const mid = Math.floor((((Number(max) - Number(min)) * 0.75) + min) / step) * step;
  if (mid > max) { return max; }
  if (mid < min) { return min; }
  return mid;
};

async function savePersonalDetailsToOmf(application, onProceed, onError) {
  sessionStorage.removeItem('sessionId');
  sessionStorage.removeItem('omfEligibilityDone');
  const personal = application.personal;
  const body = {
    payload: {
      firstName: personal.firstName || '',
      lastName: personal.lastName || '',
      idNumber: (personal.idNumber || '').replace(/\s/g, ''),
      mobileNumber: `0${(personal.mobileNumber || '').replace(/\D/g, '').slice(-9)}`,
      emailAddress: personal.email || '',
      creditConsent: config.omf.defaults.creditConsent,
      disclaimerConsent: config.omf.defaults.disclaimerConsent,
      finalise: config.omf.defaults.finalise,
      sessionId: sessionId,
      employment : {
        status: 'FUL',
        sector: 'PUBLIC',
        startDate: employmentStartDate,
        endDate:  null
      }
    },
  };

  try {
    const response = await omfRequest(config.omf.personalDetails, { method: 'POST', body: JSON.stringify(body) });
    if (response?.status === 200) {
      const data = typeof response.result?.data === 'string' ? JSON.parse(response.result.data) : response.result?.data;
      sessionStorage.setItem('sessionId', data?.sessionId || '');
      sessionStorage.setItem('applicationId', data?.applicationId || '');
      sessionStorage.setItem('mobileNumber', (personal.mobileNumber || '').replace(/\D/g, ''));
      affordibilityApiCall(application, onProceed, onError);
    } else {
      sessionStorage.setItem('omfEligibilityDone', 'true');
    }
    return response;
  } catch (e) {
    sessionStorage.setItem('omfEligibilityDone', 'true');
    return null;
  }
}

async function affordibilityApiCall(application, onProceed, onError) {
  const requestDetails = application.request;
  const grossIncome = parseFloat(requestDetails?.grossIncome || 0);
  const netIncome = parseFloat(requestDetails?.netIncome || 0);
  const expenses = parseFloat(requestDetails?.estimatedExpenses || 0);

  const body = {
    payload: {
      applicationId: sessionStorage.getItem('applicationId') || '',
      sessionId: sessionStorage.getItem('sessionId') || '',
      incomeDetails: {
        grossIncome: grossIncome,
        netIncome: netIncome,
      },
      offerDetails: {
        capitalRequired: parseInt(requestDetails?.loanAmount || 0),
        loanTerm: String(requestDetails?.loanTerm || ''),
      },
      expenseDetails: {
        groceries: 0,
        utilities: 0,
        savings: 0,
        services: 0,
        transport: 0,
        other: expenses,
        housing: 0,
        support: 0,
      },
      bankDetails: {
        bank: 'ABSA',
      },
      employmentDetails: {
        occupationStatus: 'FUL',
        sector: 'PUBLIC',
        employmentStartDate: employmentStartDate,
      },
      conditions: {
        debtLiability: 'NONE',
        retrenchment: false,
        medicalCondition: false,
      },
    },
  };

  try {
    const affordibilityResponse = await omfRequest(config.omf.affordability, { method: 'POST', body: JSON.stringify(body) });

    if (!affordibilityResponse || affordibilityResponse.status !== 200) {
      sessionStorage.setItem('omfEligibilityDone', 'true');
      onError && onError(affordibilityResponse);
      return affordibilityResponse;
    }

    return new Promise((resolve, reject) => {
      let numPolls = 0;

      const poller = setInterval(() => {
        eligibilityApiCall()
          .then(response => {
            if (!response || response.status >= 400) {
              clearInterval(poller);
              reject(new Error('Eligibility API returned null'));
              onError && onError(null);
              return;
            }

            switch (response.data.status) {
              case 'PROCEED': {
                clearInterval(poller);
                const offer = response.data.offer;
                const loanAmount = Number(offer.maximumLoan);
                const loanTerm = getInterpolatedValue(Number(offer.minimumTerm), Number(offer.maximumTerm), 12);
                const omfLoan = getLoan(loanTerm, loanAmount, {
                  minimumInitiationFee: Number(offer.minimumInitiationFee),
                  maximumInitiationFee: Number(offer.maximumInitiationFee),
                  vatRate: Number(offer.vatRate),
                  interestRate: Number(offer.interestRate),
                  creditLifeRate: Number(offer.creditLifeRate),
                  adminFee: Number(offer.adminFee),
                });
                sessionStorage.setItem('omfOffer', JSON.stringify({ ...omfLoan, minimumLoan: offer.minimumLoan, maximumLoan: offer.maximumLoan }));
                sessionStorage.setItem('omfEligibilityDone', 'true');
                resolve(response);
                onProceed && onProceed();
                return;
              }

              case 'ACTIVE_QUOTE': {
                clearInterval(poller);
                sessionStorage.setItem('omfEligibilityDone', 'true');
                resolve(response);
                onError && onError(response);
                return;
              }

              case 'OMF_STAFF': {
                clearInterval(poller);
                sessionStorage.setItem('omfEligibilityDone', 'true');
                resolve(response);
                onError && onError(response);
                return;
              }

              case 'SYSTEM_ERROR': {
                clearInterval(poller);
                sessionStorage.setItem('omfEligibilityDone', 'true');
                reject(response);
                onError && onError(response);
                return;
              }

              case 'PENDING': {
                break;
              }

              default: {
                clearInterval(poller);
                sessionStorage.setItem('omfEligibilityDone', 'true');
                resolve(response);
                onError && onError(response);
                return;
              }
            }

            if (numPolls++ > 35) {
              clearInterval(poller);
              sessionStorage.setItem('omfEligibilityDone', 'true');
              reject(new Error('Eligibility poll timed out'));
              onError && onError(null);
            }
          })
          .catch(err => {
            clearInterval(poller);
            sessionStorage.setItem('omfEligibilityDone', 'true');
            reject(err);
            onError && onError(err);
          });
      }, 5000);
    });
  } catch (e) {
    sessionStorage.setItem('omfEligibilityDone', 'true');
    onError && onError(e);
    return null;
  }
}

async function eligibilityApiCall() {
  const applicationId = sessionStorage.getItem('applicationId') || '';
  const storedSessionId = sessionStorage.getItem('sessionId') || '';

  const response = await omfRequest(
    `${config.omf.eligibility}?applicationId=${applicationId}`,
    { method: 'GET', headers: { 'x-api-sessionid': storedSessionId } },
    { showToastOnError: false },
  );
  if (!response) { return null; }
  const data = typeof response.result?.data === 'string' ? JSON.parse(response.result.data) : response.result?.data;
  return { status: response.status, data };
}

export async function finaliseApiCall() {
  const applicationId = sessionStorage.getItem('applicationId') || '';
  const sessionId = sessionStorage.getItem('sessionId') || '';

  const body = { payload: { applicationId, sessionId } };
  const response = await omfRequest(config.omf.finalise, { method: 'POST', body: JSON.stringify(body) });
  return response;
}

export async function generateOtpApiCall() {
  const applicationId = sessionStorage.getItem('applicationId') || '';
  const sessionId = sessionStorage.getItem('sessionId') || '';
  const body = { payload: { applicationId, sessionId } };
  const response = await omfRequest(config.omf.generateOtp, { method: 'POST', body: JSON.stringify(body) });
  return response;
}

export async function verifyOtpApiCall(otp) {
  const applicationId = sessionStorage.getItem('applicationId') || '';
  const sessionId = sessionStorage.getItem('sessionId') || '';

  const body = { payload: { applicationId, sessionId, otp } };
  const response = await omfRequest(config.omf.verifyOtp, { method: 'POST', body: JSON.stringify(body) });
  if (!response) { return null; }
  const data = typeof response.result?.data === 'string' ? JSON.parse(response.result.data) : response.result?.data;
  return { status: response.status, data };
}

export async function getBannerUrl() {
  const response = await omfRequest(config.omf.bannerUrl, { method: 'GET' });
  if (response?.status === 200) {
    const data = typeof response.result?.data === 'string' ? JSON.parse(response.result.data) : response.result?.data;
    return data;
  }
  return null;
}

function save() {
  return {
    async searchCustomer(application, property) {
      const value = application.personal[property];
      const fetchOptions = { method: 'GET' };

      const response = await request(`/customers/property/${property}?value=${value}`, {
        fetchOptions,
        baseURLOverwrite: null,
        tealiumErrorLogger: null,
        suppressTealiumEventOnError: false,
        showToastOnError: false,
        ignoreErrorCodes: [401, 404],
      });

      return response;
    },

    async createCustomer(application) {
      const personal = application.personal;
      const fetchOptions = {
        method: 'POST',
        body: JSON.stringify({
          email: personal.email,
          idNumber: personal.idNumber,
          clientReference: personal.clientReference,
          firstName: personal.firstName,
          lastName: personal.lastName,
        }),
      };

      const response = await request('/customers', {
        fetchOptions,
        baseURLOverwrite: null,
        tealiumErrorLogger: null,
        suppressTealiumEventOnError: false,
        showToastOnError: true,
        ignoreErrorCodes: [401],
      });

      return response;
    },

    async getQuotes(application) {
      const requestDetails = application.request;
      const gross_income = parseFloat(requestDetails.grossIncome || 0);
      const expenses = parseFloat(requestDetails.estimatedExpenses || 0);

      const fetchOptions = {
        method: 'POST',
        body: JSON.stringify({
          affiliate_id: '191',
          id_number: application.personal.idNumber,
          first_name: application.personal.firstName,
          middle_name: '',
          last_name: application.personal.lastName,
          cell_phone_number: `0${application.personal.mobileNumber.substring(3)}`,
          email: application.personal.email,
          gross_income: gross_income,
          expenses: expenses,
          net_income: gross_income - expenses,
          payment_frequency: requestDetails.incomeFrequency || income_frequencies.weekly,
          payday: 0,
          type: null,
          popi: true,
          initial_popi: false,
          intent_id: 2,
          loan_amount_required: parseInt(requestDetails.loanAmount),
          repayment_period: parseInt(requestDetails.loanTerm),
          application_method: applicationDefaults.applicationMethods.quote,
          fast_application: true,
          employed: false,
          income_source: requestDetails.incomeSource,
        }),
      };

      const response = await request('/v1/offer', {
        fetchOptions,
        baseURLOverwrite: null,
        tealiumErrorLogger: null,
        suppressTealiumEventOnError: false,
        showToastOnError: true,
        ignoreErrorCodes: [401],
      });

      return response;
    },

    async acceptOffer(application) {
      const { hashId, partnerId } = application.selectedOffer;

      const reqBody = {
        hashid: hashId,
        partnerId: partnerId,
        type: applicationDefaults.applicationMethods.quote,
      };

      const fetchOptions = {
        method: 'POST',
        body: JSON.stringify(reqBody),
      };

      const response = await request('/v1/accept', {
        fetchOptions,
        baseURLOverwrite: null,
        tealiumErrorLogger: null,
        suppressTealiumEventOnError: false,
        showToastOnError: true,
        ignoreErrorCodes: [401],
      });

      return response;
    },
  };
}

export const compareBackend = () => ({
  async fetchQuotes(application) {
    const client = save();
    return client.getQuotes(application);
  },

  async saveApplication(application) {
    if (application.canSave) {
      const client = save();
      const errorOutcome = {
        successful: false,
        techError: false,
        message: errorMessages.technical.general,
      };

      try {
        switch (application.lastStep) {
          case lendingOnboardingSteps.personal_details: {
            // OMF personal details — fire in parallel, don't block the existing flow.
            // Offers page polls sessionStorage for omfOffer and adds it dynamically.
            savePersonalDetailsToOmf(application, null, null);

            // Search by email
            const emailResponse = await client.searchCustomer(application, 'email');

            if (emailResponse.status >= 500) {
              return errorOutcome;
            }

            // If found, just mark as found - NO need to store clientId
            if (emailResponse.status === 200) {
              Cache.save(storageKeys.compare_application, application);
              break;
            }

            // If not found by email, try ID - NO need to store clientId
            if (emailResponse.status === 404) {
              const idNumberResponse = await client.searchCustomer(application, 'idNumber');

              if (idNumberResponse.status >= 500) {
                return errorOutcome;
              }

              if (idNumberResponse.status === 200) {
                Cache.save(storageKeys.compare_application, application);
                break;
              }
            }

            // Create customer - NO need to store clientId or apiToken
            const customerResponse = await client.createCustomer(application);
            if (customerResponse.status >= 400) {
              return errorOutcome;
            }

            // Backend now tracks the customer internally
            // We just continue with the flow using email/idNumber
            Cache.save(storageKeys.compare_application, application);
            break;
          }

          case lendingOnboardingSteps.offers_details: {
            const acceptOfferResponse = await client.acceptOffer(application);
            if (acceptOfferResponse.status >= 400) {
              return errorOutcome;
            }

            const acceptOffer = acceptOfferResponse.result;
            if (acceptOffer && acceptOffer.ok) {
              break;
            }
            break;
          }

          case lendingOnboardingSteps.thank_you: {
            await client.acceptOffer(application);
            break;
          }

          default:
            return { successful: true, message: lendingApplicationMessages.actions.unchanged };
        }
      } catch (error) {
        console.error(error);
        errorOutcome.techError = true;
        errorOutcome.message = error;
        return errorOutcome;
      }
    }

    return { successful: true, message: lendingApplicationMessages.actions.unchanged };
  },
});
